/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Select } from 'antd';
import { SelectProps } from 'antd/lib/select';
import {
  MouseEvent,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { useIntl } from 'react-intl';

// local imports
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { DbFile } from '~src/types/db';
import FileVersionsContext from '~src/contexts/FileVersionsContext';

export interface VersionSelectProps extends Partial<SelectProps<any>> {
  file: DbFile;
  manual?: boolean;
}

/** File version select */
export const VersionSelect: React.FC<VersionSelectProps> = ({
  file,
  ...selectProps
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const { fileVersionsContext, setFileVersionsContext } = useContext(
    FileVersionsContext
  );

  // Track previous length of file.versions
  const prevVersionsLength = useRef<number>(file?.versions?.length || 0);

  const options = useMemo(
    () =>
      (file?.versions || []).map((version, i, list) => ({
        key: `file-${file?.id}-v${version?.id}`,
        label: version?.revision || list?.length - i,
        value: version?.id,
      })),
    [file?.versions, file?.id]
  );

  const handleClick = useCallback((e: MouseEvent) => e.stopPropagation(), []);

  // When the user selects a version, spread its properties and add manual flag.
  const handleSelect = useCallback(
    (value: string) => {
      const selectedVersion = file?.versions?.find((v) => v?.id === value);
      if (selectedVersion) {
        setFileVersionsContext((prevVersionCtx) => ({
          ...prevVersionCtx,
          [file?.id]: { ...selectedVersion, manual: true },
        }));
      }
    },
    [file?.id, file?.versions, setFileVersionsContext]
  );

  // USE EFFECT FROM STAGE
  useEffect(() => {
    if (file.versions?.length) {
      // Compute the new latest version
      const newLatest = file.versions.reduce((prev, current) =>
        (prev.revision ?? 0) > (current.revision ?? 0) ? prev : current
      );
      // Read the current selection from context.
      const currentSelectionObj = fileVersionsContext[file.id];
      const currentVersion = currentSelectionObj;
      // Check if we need to update: no version, newer revision, OR SVF model state changed
      const shouldUpdate =
        !currentVersion ||
        (!currentVersion.manual &&
          Number(currentVersion.revision ?? 0) <
            Number(newLatest.revision ?? 0)) ||
        (!currentVersion.manual &&
          currentVersion.extension_svf_model?.state !==
            newLatest.extension_svf_model?.state);

      if (shouldUpdate) {
        setFileVersionsContext((prevCtx) => ({
          ...prevCtx,
          [file.id]: { ...newLatest, manual: false },
        }));
      }

      prevVersionsLength.current = file.versions.length;
    }
  }, [
    file.id,
    file.versions,
    fileVersionsContext[file.id],
    setFileVersionsContext,
  ]);

  const currentSelection = fileVersionsContext[file?.id];
  const selectedIndex = currentSelection
    ? options.findIndex((opt) => opt?.value === currentSelection?.id)
    : 0;
  const selected = selectedIndex > -1 ? options[selectedIndex] : options[0];

  const setSelectBackgroundColor = () => {
    if (selectedIndex < 1) {
      return window.sessionStorage.getItem('appState') !== 'timeMachine'
        ? '#fff !important'
        : '#525a7b  !important';
    } else {
      return window.sessionStorage.getItem('appState') !== 'timeMachine'
        ? '#fff5be !important'
        : 'rgba(255, 255, 255, 0.2) !important';
    }
  };

  return (
    <Select
      css={{
        width: '100%',
        '&.ant-select-single > .ant-select-selector': {
          background: setSelectBackgroundColor(),
        },
      }}
      disabled={options.length < 2}
      options={options}
      onClick={handleClick}
      onSelect={handleSelect}
      value={selected?.value}
      {...selectProps}
    />
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {},
});

// TENDER DEV USE EFFECT
// useEffect(() => {
//   if (file?.versions?.length) {
//     // Compute the new latest version
//     const newLatest = file?.versions.reduce((prev, current) =>
//       (prev?.revision ?? 0) > (current?.revision ?? 0) ? prev : current
//     );
//     // Read the current selection from context.
//     const currentSelectionObj = fileVersionsContext[file.id];
//     const currentVersion = currentSelectionObj;
//     // If no selection exists, or if the current auto-selected version is older than newLatest, update.
//     if (
//       !currentVersion ||
//       (!currentVersion?.manual &&
//         Number(currentVersion?.revision ?? 0) <
//         Number(newLatest?.revision ?? 0))
//     ) {
//       setFileVersionsContext((prevCtx) => ({
//         ...prevCtx,
//         [file.id]: { ...newLatest, manual: false },
//       }));
//     }
//
//     prevVersionsLength.current = file?.versions?.length;
//   }
// }, [
//   file?.id,
//   file?.versions,
//   fileVersionsContext[file?.id],
//   setFileVersionsContext,
// ])
